<script setup lang="ts">
/* ============================================================
   Reader — the application shell

   Wires the chrome, welcome screen, document views, feedback modal,
   drag/drop and paste ingestion, outline, search, and toasts.

   The reader keeps every open tab's DocView mounted (v-show), as
   the prototype kept every .doc-view in the DOM — that preserves
   scroll position, rendered highlights, and card layout when
   switching tabs without re-rendering the prose.

   Shared-plan keys stay in the URL fragment and never enter router state.
   ============================================================ */

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import type { Tab, Comment } from '@/types'
import { useTheme } from '@/composables/useTheme'
import { useTabs } from '@/composables/useTabs'
import { usePersistence, setOnSaved } from '@/composables/usePersistence'
import { useIdentity } from '@/composables/useIdentity'
import { pushToast } from '@/composables/useToasts'
import { fetchReviewSnapshot } from '@/composables/useReviewSnapshot'
import {
  fetchPlan,
  loadEmbeddedPlan,
  PlanLoadFailure,
  type PlanLoadError,
} from '@/composables/usePublish'
import { setPlanOrigin } from '@/composables/useShare'
import { deriveTitle } from '@/composables/utils'
import { buildWelcomeTab, buildDocsTab } from '@/composables/useSpecialTabs'
import { recordMetric } from '@/composables/useAnalytics'
import TabBar from '@/components/TabBar.vue'
import HomeView from '@/components/HomeView.vue'
import PasteModal from '@/components/PasteModal.vue'
import InstallModal from '@/components/InstallModal.vue'
import DocView from '@/components/DocView.vue'
import HandoffModal from '@/components/HandoffModal.vue'
import OutlinePanel from '@/components/OutlinePanel.vue'
import Toasts from '@/components/Toasts.vue'

// /plan/:slug deep links arrive here; the decryption key is read separately
// from location.hash by the crypto module, never via routing. `embedded` is set
// only when mounted from a downloaded portable-HTML snapshot (see main.ts): the
// plan is decrypted from inlined page globals and the publish/share chrome —
// which needs a server — is hidden.
const props = defineProps<{ slug?: string; embedded?: boolean; firstRunPreview?: boolean }>()

useTheme()
const {
  tabs,
  activeHash,
  activeTab,
  openFiles,
  openMarkdown,
  openFromHash,
  hydrateTabs,
  activateTab,
  closeTab,
  resetTabs,
} = useTabs()
const { ready, listAll, clearAll, putTab } = usePersistence()

const fileInput = ref<HTMLInputElement | null>(null)
const dragging = ref(false)
const showHandoff = ref(false)
const showPaste = ref(false)
const showInstall = ref(false)
const showOutline = ref(false)
const outlineItems = ref<{ level: number; text: string }[]>([])
const recent = ref<Tab[]>([])
const docViews = ref<InstanceType<typeof DocView>[]>([])
const welcomeView = ref<InstanceType<typeof DocView> | null>(null)
const planError = ref<PlanLoadError | null>(null)

// ---- reviewer identity (the corner chip; name shown on this person's comments) ----
const {
  id: myId,
  name: myName,
  color: myColor,
  setName,
  highlight: myHighlight,
  swatches,
  setHighlight,
} = useIdentity()
// Clicking the chip opens a small identity popover: name + highlight color.
const identityOpen = ref(false)
const nameDraft = ref('')
const nameInput = ref<HTMLInputElement | null>(null)
function openIdentity(): void {
  nameDraft.value = myName.value
  identityOpen.value = true
  void nextTick(() => nameInput.value?.select())
}
/** Close the popover, saving the typed name unless `save` is false. */
function closeIdentity(save = true): void {
  if (!identityOpen.value) return
  if (save) applyName()
  identityOpen.value = false
}
function toggleIdentity(): void {
  if (identityOpen.value) closeIdentity()
  else openIdentity()
}
function applyName(): void {
  const before = myName.value
  setName(nameDraft.value)
  if (myName.value === before) return
  // Authorship is a local label, not a shared identity registry. Update our
  // existing snapshots in-place so copied feedback uses the new name too.
  for (const t of tabs.value) {
    for (const comment of t.comments) {
      if (comment.author?.id === myId.value) comment.author.name = myName.value
      for (const reply of comment.replies ?? []) {
        if (reply.author.id === myId.value) reply.author.name = myName.value
      }
    }
    void putTab(t)
    rerenderTab(t.hash)
  }
}

// Two pinned, always-present documents bracket the user's own tabs:
//  - Home (leftmost): one tab whose content graduates with history — the
//    "Welcome.md" intro with demo comments on a first visit, or the
//    workflow-focused "Welcome back" dashboard once you've opened a doc.
//  - Docs.md (right-justified): the reference docs, opened like any doc.
// Both are ephemeral (never persisted / never in recents). Exactly one view is
// active at a time: the Home tab, the Docs tab, or one of the user's docs.
const welcomeActive = ref(true)
const docsActive = ref(false)
// True from the very first render whenever we're about to open a shared plan
// (a /plan/<slug> deep link or an embedded snapshot). Gating the Welcome/Home
// surfaces on this prevents the dashboard flashing for the beat between mount
// and the fetch+decrypt resolving — the deep-link FOUC. Cleared once the plan
// renders or its load errors. Initialized from props synchronously so even the
// first paint is suppressed; the late-arriving-slug watcher sets it too.
const loadingPlan = ref((!!props.slug || !!props.embedded) && !props.firstRunPreview)
// Same idea for a plain reload: when the saved session was on a doc (or Docs),
// keep Home hidden until restoreSession has reopened it — otherwise the
// dashboard paints for the beat while the tabs hydrate from IndexedDB.
const SESSION_KEY = 'md-reader-session'
const bootSession = !props.slug && !props.embedded && !props.firstRunPreview ? readSavedSession() : null
const restoring = ref(!!bootSession?.open?.length && bootSession.surface !== 'home')
// First run = nothing reviewed yet. Driven by history (no recents), not a
// one-shot "visited" flag — so the Welcome.md intro with its demo comments
// stays until you've actually opened a doc, and returns after a Clear history.
const firstRun = computed(() => props.firstRunPreview || recent.value.length === 0)
const welcomeTab = ref<Tab | null>(null)
const docsTab = ref<Tab | null>(null)
const welcomeName = 'Home'
// On first visit the Welcome tab is the demo doc (DocView); after that it's the
// dashboard (HomeView).
const showWelcomeBack = computed(
  () => welcomeActive.value && !firstRun.value && !loadingPlan.value && !restoring.value,
)

function goWelcome(): void {
  welcomeActive.value = true
  docsActive.value = false
  showOutline.value = false
}
async function goDocs(): Promise<void> {
  if (!docsTab.value) await loadDocsTab()
  if (!docsTab.value) return
  welcomeActive.value = false
  docsActive.value = true
  showOutline.value = false
}
function openDocTab(hash: string): void {
  leaveSpecial()
  activateTab(hash)
}
/** Deselect the pinned Welcome/Docs tabs (for open paths that activate a doc
 *  internally, e.g. file drop / recent open). */
function leaveSpecial(): void {
  welcomeActive.value = false
  docsActive.value = false
}

const PLAN_ERROR_TITLES: Record<PlanLoadError, string> = {
  not_found: 'Plan not found',
  key_absent: 'Missing decryption key',
  bad_key: "Couldn't decrypt this plan",
  fetch_failed: "Couldn't load this plan",
  malformed: 'This link looks corrupted',
}
const PLAN_ERROR_MESSAGES: Record<PlanLoadError, string> = {
  not_found:
    "There's no plan at this link. The address may be mistyped, or the plan was never uploaded.",
  key_absent:
    'This link is missing the key after the “#”. Without it the plan can’t be decrypted — ask whoever shared it for the complete link.',
  bad_key:
    'The key in this link doesn’t match the plan. It was likely copied incompletely — ask whoever shared it for the full link.',
  fetch_failed: 'We couldn’t reach the server. Check your connection and try the link again.',
  malformed: 'Part of this link appears to be damaged or incomplete — ask for a fresh copy.',
}
const planErrorTitle = computed(() =>
  planError.value ? PLAN_ERROR_TITLES[planError.value] : '',
)
const planErrorMessage = computed(() =>
  planError.value ? PLAN_ERROR_MESSAGES[planError.value] : '',
)

async function refreshRecent(): Promise<void> {
  recent.value = await listAll()
}

// ---- session restore: open documents + the active surface -----------------
function persistSession(): void {
  // Never write in embedded snapshots or the /first-run preview — the preview
  // must not clobber the user's real saved session.
  if (props.embedded || props.firstRunPreview) return
  const surface = welcomeActive.value ? 'home' : docsActive.value ? 'docs' : 'doc'
  try {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ open: tabs.value.map((t) => t.hash), active: activeHash.value, surface }),
    )
  } catch {
    /* storage may be unavailable; the session just won't restore next load */
  }
}
interface SavedSession {
  open?: string[]
  active?: string | null
  surface?: string
}
function readSavedSession(): SavedSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as SavedSession) : null
  } catch {
    return null
  }
}
/** Reopen the previously-open tabs and land on the surface (home/docs/a doc)
 *  the user left on. Skipped for deep links and embedded snapshots. */
async function restoreSession(): Promise<void> {
  try {
    await reopenSaved()
  } finally {
    restoring.value = false
  }
}
async function reopenSaved(): Promise<void> {
  const saved = readSavedSession()
  if (!saved?.open?.length) return
  await hydrateTabs(saved.open)
  const haveActive = !!saved.active && tabs.value.some((t) => t.hash === saved.active)
  if (haveActive) activateTab(saved.active as string)
  if (saved.surface === 'docs') await goDocs()
  else if (saved.surface === 'doc' && haveActive) leaveSpecial()
  else goWelcome()
}
// Re-persist whenever the open set, the active doc, or the surface changes.
watch(
  [() => tabs.value.map((t) => t.hash).join(','), activeHash, welcomeActive, docsActive],
  persistSession,
)

/* ---- browser history: make in-app navigation back/forward-navigable ----
   The reader is a tabbed SPA, so switching surface (home / docs / a doc) is
   pure reactive state. Without this, the back button can't return you to Home
   after opening a recent. We push one history entry per distinct view and
   restore the view on popstate. The URL path is intentionally left unchanged:
   local docs have no shareable route, and a shared plan's key must stay in the
   fragment — so only history.state carries the view key. */
let navReady = false

function currentNavKey(): string {
  if (docsActive.value) return 'docs'
  if (!welcomeActive.value && activeHash.value) return `doc:${activeHash.value}`
  return 'home'
}

/** Mirror the current view into history. On popstate the browser has already
 *  swapped history.state to the target entry, so the de-dup check below makes
 *  applyNavKey's view change a no-op push. Merges existing state to preserve
 *  vue-router's bookkeeping fields. */
function syncNavState(replace = false): void {
  const navKey = currentNavKey()
  if (!replace && history.state?.navKey === navKey) return
  const next = { ...history.state, navKey }
  if (replace) history.replaceState(next, '')
  else history.pushState(next, '')
}

/** Apply a history entry's view key back onto the reader (popstate). */
async function applyNavKey(key: string): Promise<void> {
  if (key === 'docs') {
    await goDocs()
  } else if (key.startsWith('doc:')) {
    const hash = key.slice(4)
    await openFromHash(hash)
    if (activeHash.value === hash) leaveSpecial()
    else goWelcome() // the doc was closed/cleared since this entry was made
  } else {
    goWelcome()
  }
}

function onPopState(): void {
  if (!navReady) return
  void applyNavKey((history.state?.navKey as string) || 'home')
}

watch([welcomeActive, docsActive, activeHash], () => {
  if (navReady) syncNavState()
})

// Re-render an already-mounted DocView (used after seeding shared comments).
function rerenderTab(hash: string): void {
  for (const v of docViews.value) {
    const view = v as unknown as { hash?: string; rerenderFromState?: () => void }
    if (view && view.hash === hash) view.rerenderFromState?.()
  }
}

// ---- outline: the active doc's heading structure as a jump list ----
type DocViewApi = {
  hash?: string
  getOutline?: () => { level: number; text: string }[]
  scrollToHeading?: (index: number) => void
}
function activeDocView(): DocViewApi | undefined {
  return docViews.value.find((v) => (v as unknown as DocViewApi).hash === activeHash.value) as
    | DocViewApi
    | undefined
}
function refreshOutline(): void {
  outlineItems.value = activeDocView()?.getOutline?.() ?? []
}
function toggleOutline(): void {
  showOutline.value = !showOutline.value
  if (showOutline.value) refreshOutline()
}
function jumpToHeading(index: number): void {
  activeDocView()?.scrollToHeading?.(index)
  showOutline.value = false
}
// Dismiss the outline when you click anywhere outside the dock (the FAB + panel).
function onOutsideClick(e: MouseEvent): void {
  const target = e.target as HTMLElement | null
  if (identityOpen.value && !target?.closest('.identity-chip')) closeIdentity()
  if (showOutline.value && !target?.closest('.outline-dock')) showOutline.value = false
}
// Keep the outline in sync if you switch tabs while it's open.
watch(activeHash, () => {
  if (showOutline.value) refreshOutline()
})

// A slug that arrives after mount (route resolved late, or client-side nav to a
// /plan/<slug> while this same Reader instance is reused) still loads the plan.
// Non-immediate: the at-mount case is handled in onMounted, so this never
// double-fires for the initial value.
watch(
  () => props.slug,
  (slug) => {
    if (slug && !props.embedded && !props.firstRunPreview) {
      // A slug arriving post-mount means we're navigating into a shared plan:
      // re-arm the loading gate so the Welcome surface doesn't flash again.
      loadingPlan.value = true
      void loadSharedPlan(slug)
    }
  },
)

// ---- /plan/:slug deep link: fetch ciphertext, decrypt with the fragment key ----
async function loadSharedPlan(slug: string): Promise<void> {
  planError.value = null
  try {
    const { markdown, key, comments, publishedAt } = await fetchPlan(slug, location.hash)
    // The slug is opaque BIP39 words; the plan's first heading is the real name.
    const name = deriveTitle(markdown) ?? `${slug}.md`
    // An agent may have seeded an immutable thread snapshot alongside a revised
    // plan. Read it once; failure never prevents the plan itself from opening.
    let snapshot: Comment[] = []
    try {
      snapshot = await fetchReviewSnapshot(slug, key)
    } catch {
      pushToast('The plan opened, but its prior comment context could not be loaded.', 'error')
    }
    const hash = await openMarkdown(markdown, name, [...comments, ...snapshot], {
      publishedAt: publishedAt ?? undefined,
    })
    // The key arrived in the fragment (never the server); keep it so the
    // recipient can re-share this same plan.
    setPlanOrigin(hash, { slug, key })
    openDocTab(hash)
    recordMetric('plan_opened')
    if (comments.length || snapshot.length) {
      await nextTick()
      rerenderTab(hash)
    }
  } catch (e) {
    planError.value = e instanceof PlanLoadFailure ? e.code : 'fetch_failed'
  } finally {
    // Plan rendered (welcomeActive now false) or errored (banner shows) —
    // either way, stop suppressing the Welcome/Home surface.
    loadingPlan.value = false
  }
}

// ---- portable snapshot: decrypt the plan inlined into the downloaded file ----
async function loadEmbedded(): Promise<void> {
  planError.value = null
  const g = window as unknown as { __REDLINE_PAYLOAD__?: string; __REDLINE_KEY__?: string }
  try {
    const { markdown, comments } = await loadEmbeddedPlan(g.__REDLINE_PAYLOAD__ || '', g.__REDLINE_KEY__ || '')
    const name = deriveTitle(markdown) ?? 'shared-plan.md'
    const hash = await openMarkdown(markdown, name, comments)
    openDocTab(hash)
    if (comments.length) {
      await nextTick()
      rerenderTab(hash)
    }
  } catch (e) {
    planError.value = e instanceof PlanLoadFailure ? e.code : 'malformed'
  } finally {
    loadingPlan.value = false
  }
}

// ---- Docs.md: fetch the Worker's origin-rewritten /docs.md into the pinned,
// ephemeral Docs tab (built once, then re-shown on subsequent clicks). ----
async function loadDocsTab(): Promise<void> {
  try {
    const resp = await fetch('/docs.md')
    if (!resp.ok) throw new Error(String(resp.status))
    const text = await resp.text()
    docsTab.value = buildDocsTab(text)
  } catch {
    pushToast('Could not load the docs — they are served by the redline.ink Worker.', 'error')
  }
}

// ---- open markdown pasted/typed (from the + menu's Paste Markdown modal) ----
async function openPastedText(text: string): Promise<void> {
  if (!text.trim()) return
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  const hash = await openMarkdown(text, `pasted-${stamp}.md`)
  openDocTab(hash)
  await refreshRecent()
}

// ---- + menu actions ----
function onPasteMarkdown(): void {
  showPaste.value = true
}
async function onPasteSubmit(text: string): Promise<void> {
  showPaste.value = false
  await openPastedText(text)
}
// Start a new document straight from the clipboard contents.
async function openFromClipboard(): Promise<void> {
  try {
    const text = await navigator.clipboard.readText()
    if (!text.trim()) {
      pushToast('Clipboard is empty.', 'error')
      return
    }
    await openPastedText(text)
  } catch {
    // Permission denied / unsupported — fall back to the paste modal.
    pushToast('Could not read the clipboard — paste it here instead.')
    showPaste.value = true
  }
}

// ---- file open ----
function chooseFile(): void {
  fileInput.value?.click()
}
async function onFilePick(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement
  if (input.files && input.files.length) {
    leaveSpecial()
    await openFiles(input.files)
  }
  input.value = ''
  await refreshRecent()
}

// ---- recent list / close / clear ----
async function onActivate(hash: string): Promise<void> {
  openDocTab(hash)
}
async function onClose(hash: string): Promise<void> {
  if (closeTab(hash)) {
    // Closing the last user doc falls back to the pinned Welcome tab.
    if (!activeHash.value) goWelcome()
    await refreshRecent()
  }
}
async function onOpenRecent(hash: string): Promise<void> {
  leaveSpecial()
  await openFromHash(hash)
}
async function onClearHistory(): Promise<void> {
  if (!confirm('Delete all reviewed documents and their comments? This cannot be undone.')) return
  await clearAll()
  resetTabs()
  showHandoff.value = false
  await refreshRecent()
}

// ---- drag + drop (counter logic ported from prototype) ----
let dragCounter = 0
function hasFiles(e: DragEvent): boolean {
  return !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')
}
function onDragEnter(e: DragEvent): void {
  if (!hasFiles(e)) return
  dragCounter++
  dragging.value = true
}
function onDragLeave(e: DragEvent): void {
  if (!hasFiles(e)) return
  dragCounter--
  if (dragCounter <= 0) {
    dragCounter = 0
    dragging.value = false
  }
}
function onDragOver(e: DragEvent): void {
  if (!hasFiles(e)) return
  e.preventDefault()
}
async function onDrop(e: DragEvent): Promise<void> {
  if (!hasFiles(e)) return
  e.preventDefault()
  dragCounter = 0
  dragging.value = false
  if (e.dataTransfer?.files && e.dataTransfer.files.length) {
    leaveSpecial()
    await openFiles(e.dataTransfer.files)
  }
  await refreshRecent()
}

// ---- paste markdown to open ----
async function onPaste(e: ClipboardEvent): Promise<void> {
  // Ignore pastes into inputs/textareas (handoff editors, comment notes).
  const t = e.target as HTMLElement
  if (t && (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || t.isContentEditable)) return
  const text = e.clipboardData?.getData('text/plain') ?? ''
  if (!text.trim()) return
  e.preventDefault()
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  const hash = await openMarkdown(text, `pasted-${stamp}.md`)
  openDocTab(hash)
  await refreshRecent()
}

// ---- selection → comment draft (delegate to DocViews) ----
// Each DocView no-ops unless it is the active one (it checks props.active),
// so a broadcast is safe and matches the prototype's single mouseup listener.
function onMouseUp(): void {
  for (const v of docViews.value) {
    if (v) v.maybeStartDraftFromSelection()
  }
  // The pinned Welcome doc lives outside the v-for, so broadcast to it too —
  // each view no-ops unless it is the active one.
  welcomeView.value?.maybeStartDraftFromSelection()
}

// ---- keyboard: Cmd+K handoff, font shortcuts ----
const { bumpFont, resetFont } = useTheme()
function onKeydown(e: KeyboardEvent): void {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    if (!activeTab.value || props.embedded || welcomeActive.value || docsActive.value) return
    e.preventDefault()
    showHandoff.value = !showHandoff.value
    return
  }
  if (e.key === 'Escape' && identityOpen.value) {
    closeIdentity(false)
    return
  }
  if (e.key === 'Escape' && showHandoff.value) {
    showHandoff.value = false
    return
  }
  if (e.key === 'Escape' && showOutline.value) {
    showOutline.value = false
    return
  }
  if (e.metaKey || e.ctrlKey) {
    if (e.key === '=' || e.key === '+') {
      e.preventDefault()
      bumpFont(+1)
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault()
      bumpFont(-1)
    } else if (e.key === '0') {
      e.preventDefault()
      resetFont()
    }
  }
}

onMounted(async () => {
  setOnSaved(() => void refreshRecent())
  try {
    await ready()
    await refreshRecent()
    if (props.embedded) {
      await loadEmbedded()
    } else {
      // Build the ephemeral Welcome.md (intro + demo comments) up front so it's
      // ready whenever firstRun (no recents) is true — the Home tab shows it on a
      // clean slate and the "Welcome back" dashboard once history exists.
      welcomeTab.value = buildWelcomeTab()
      if (props.firstRunPreview) {
        // Hidden /first-run preview: stay on the welcome doc, ignore stored
        // session/recents entirely so the empty state shows in isolation.
        welcomeActive.value = true
      } else if (props.slug) await loadSharedPlan(props.slug)
      else await restoreSession()
      // Seed the current view as this history entry, then track changes.
      syncNavState(true)
      navReady = true
      window.addEventListener('popstate', onPopState)
    }
  } finally {
    // Never leave the surface blank if storage failed before the restore ran.
    restoring.value = false
  }
  document.addEventListener('dragenter', onDragEnter)
  document.addEventListener('dragleave', onDragLeave)
  document.addEventListener('dragover', onDragOver)
  document.addEventListener('drop', onDrop)
  document.addEventListener('paste', onPaste)
  document.addEventListener('mouseup', onMouseUp)
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('mousedown', onOutsideClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('dragenter', onDragEnter)
  document.removeEventListener('dragleave', onDragLeave)
  document.removeEventListener('dragover', onDragOver)
  document.removeEventListener('drop', onDrop)
  document.removeEventListener('paste', onPaste)
  document.removeEventListener('mouseup', onMouseUp)
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('mousedown', onOutsideClick)
  window.removeEventListener('popstate', onPopState)
})
</script>

<template>
  <div class="app">
    <TabBar
      :tabs="tabs"
      :active-hash="activeHash"
      :welcome-active="welcomeActive && !restoring"
      :welcome-name="welcomeName"
      :docs-active="docsActive"
      :embedded="embedded"
      @open-file="chooseFile"
      @paste-markdown="onPasteMarkdown"
      @paste-clipboard="openFromClipboard"
      @welcome="goWelcome"
      @docs="goDocs"
      @install="showInstall = true"
      @activate="onActivate"
      @close="onClose"
    />

    <main class="main">
      <div v-if="planError" class="plan-error" role="alert">
        <div class="plan-error-card">
          <span class="plan-error-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <h2 class="plan-error-title">{{ planErrorTitle }}</h2>
          <p class="plan-error-msg">{{ planErrorMessage }}</p>
          <div class="plan-error-actions">
            <button class="btn btn-primary btn-md" @click="planError = null">Dismiss</button>
          </div>
        </div>
      </div>

      <!-- Shared-plan load: holds the surface quiet while the ciphertext is
           fetched + decrypted, so the Welcome/Home screen never flashes on a
           deep link. Replaced by the plan's DocView the moment it resolves. -->
      <div v-if="loadingPlan && !planError" class="plan-loading" aria-live="polite">
        <span class="plan-loading-spinner" aria-hidden="true"></span>
        <span class="plan-loading-text">Decrypting shared plan…</span>
      </div>

      <!-- Pinned Welcome — first visit: Welcome.md with demo comments. -->
      <DocView
        v-if="welcomeTab"
        ref="welcomeView"
        :tab="welcomeTab"
        :active="welcomeActive && firstRun && !loadingPlan && !restoring"
        ephemeral
      />

      <!-- Pinned Welcome — returning visit: the workflow-focused dashboard. -->
      <HomeView
        v-show="showWelcomeBack"
        :recent="recent"
        :active="showWelcomeBack && !showPaste && !showInstall"
        @open="onOpenRecent"
        @clear="onClearHistory"
        @open-file="chooseFile"
        @paste-clipboard="openFromClipboard"
        @paste-text="openPastedText"
        @install="showInstall = true"
      />

      <!-- Pinned Docs.md (right-justified tab), fetched on first open.
           Reading-only: no comment gutter, centered prose. -->
      <DocView v-if="docsTab" :tab="docsTab" :active="docsActive" ephemeral plain />

      <DocView
        v-for="tab in tabs"
        :key="tab.hash"
        ref="docViews"
        :tab="tab"
        :active="!welcomeActive && !docsActive && tab.hash === activeHash"
      />

      <div
        v-show="!welcomeActive && !docsActive && activeTab && !embedded"
        class="floating-actions"
      >
        <button
          class="btn btn-dark btn-lg btn-elev handoff-fab"
          title="Send your comments back to the agent"
          @click="showHandoff = true"
        >
          <span class="handoff-fab-icon" aria-hidden="true">↗</span>
          <span class="handoff-fab-label">Send feedback</span>
        </button>
      </div>

      <div
        v-show="!welcomeActive && !docsActive && activeTab && !embedded"
        class="identity-chip"
        :style="{ '--me-color': myColor }"
      >
        <div
          v-if="identityOpen"
          class="identity-popover"
          role="dialog"
          aria-label="Your identity"
        >
          <label class="identity-pop-label" for="identity-name-input">Name</label>
          <input
            id="identity-name-input"
            ref="nameInput"
            v-model="nameDraft"
            class="identity-input"
            maxlength="40"
            spellcheck="false"
            @keydown.enter.prevent="closeIdentity()"
            @keydown.esc.prevent.stop="closeIdentity(false)"
          />
          <div class="identity-pop-label">Highlight color</div>
          <div class="identity-swatches" role="radiogroup" aria-label="Highlight color">
            <button
              v-for="s in swatches"
              :key="s.id"
              class="identity-swatch"
              :class="{ 'is-active': s.id === myHighlight }"
              :style="{ '--swatch': s.color }"
              role="radio"
              :aria-checked="s.id === myHighlight"
              :aria-label="s.name"
              :title="s.id === 'auto' ? 'Automatic — the color assigned to you' : s.name"
              @click="setHighlight(s.id)"
            ></button>
          </div>
        </div>
        <button
          class="identity-chip-btn"
          :class="{ 'is-open': identityOpen }"
          :title="`You're commenting as ${myName} — click to change name or color`"
          aria-haspopup="dialog"
          :aria-expanded="identityOpen"
          @click="toggleIdentity"
        >
          <span class="identity-dot" aria-hidden="true"></span>
          <span class="identity-name">{{ myName }}</span>
        </button>
      </div>

      <div
        v-show="!welcomeActive && !docsActive && activeTab && !embedded"
        class="outline-dock"
      >
        <button
          class="btn btn-default btn-md btn-elev outline-fab"
          :class="{ 'is-active': showOutline }"
          :aria-pressed="showOutline"
          title="Document outline — jump between headings"
          @click="toggleOutline"
        >
          <span class="outline-fab-icon" aria-hidden="true">☰</span>
          <span class="outline-fab-label">Outline</span>
        </button>
        <OutlinePanel
          v-if="showOutline"
          :items="outlineItems"
          @jump="jumpToHeading"
          @close="showOutline = false"
        />
      </div>

      <HandoffModal
        v-if="showHandoff && activeTab && !embedded"
        :tab="activeTab"
        @close="showHandoff = false"
      />

      <PasteModal v-if="showPaste" @submit="onPasteSubmit" @close="showPaste = false" />

      <InstallModal v-if="showInstall" @close="showInstall = false" />
    </main>

    <div class="drop-overlay" :class="{ 'is-visible': dragging }">
      <div class="drop-msg">Drop markdown file to open</div>
    </div>

    <Toasts />

    <input
      ref="fileInput"
      type="file"
      accept=".md,.markdown,text/markdown,text/plain"
      multiple
      hidden
      @change="onFilePick"
    />
  </div>
</template>
